import { AHText, AHWidgetTitle, Box, TouchableBox } from "arkh";
import React, { useCallback, useMemo } from "react";
import { formatValue } from "./formatNumber";
import { DateTime } from "luxon";
import { GraphContainer } from "../Graph/Graph";
import { GraphNumber, GraphNumberData } from "./GraphNumber";
import { SingleTaskInProgress } from "../SingleTaskInProgress";
import { useGroupNumberTaskExecution } from "../useGroupNumberTaskExecution";

import { getDateSequenceFormatting } from "./getDateSequenceFormatting";

import { View } from "react-native";
import { SingleSectionTitle } from "../SingleSectionTitle";
import { SmallInProgress } from "./SmallInProgress";
import { getGoalBars } from "./getGoalBars";
import { Section } from "../useWidgetTaskSingleSections";
import { graphql, useFragment } from "react-relay";
import { WidgetTaskSingleNumberOccasionPage_taskNumber$key } from "./__generated__/WidgetTaskSingleNumberOccasionPage_taskNumber.graphql";
import { WidgetTaskSingleGroupingMode } from "../__generated__/WidgetTaskSingle_widgetTaskSingle.graphql";

const WidgetTaskSingleNumberOccasionPageC = ({
  task: taskKey,
  sections,
  large,
  onPress,
  maxOccasions,
  maxParts,
  interactive,
  grouping,
}: {
  onPress: (sequenceNumbers: number[]) => void;
  large?: boolean;
  task: WidgetTaskSingleNumberOccasionPage_taskNumber$key;
  sections: Section[];
  maxOccasions?: number;
  maxParts?: number;
  interactive?: boolean;
  grouping: WidgetTaskSingleGroupingMode;
}) => {
  const task = useFragment(
    graphql`
      fragment WidgetTaskSingleNumberOccasionPage_taskNumber on TaskNumber {
        id
        unit
        title
        routine {
          id
          title
          type
          schedule {
            rules {
              freq
            }
          }
        }
        occasions(first: 8) {
          edges {
            node {
              id
              occasion {
                id
                startAt
                endAt
                sequenceNumber
              }
              taskExecutions {
                numberValue
                sectionId
                goals {
                  lessThan
                  greaterThan
                }
                ...useGroupNumberTaskExecution_taskExecutionNumber
                updatedAt
              }
            }
          }
        }
      }
    `,
    taskKey
  );

  const sectionMap = useMemo(() => {
    return new Map(sections.map((section) => [section.id, section]));
  }, [sections]);
  const inProgressOccasion = task.occasions.edges[0]?.node;

  const { groups, total, lastUpdated } = useGroupNumberTaskExecution(
    inProgressOccasion?.taskExecutions ?? [],
    sectionMap,
    maxParts ?? (large === false ? 999 : undefined),
    grouping
  );

  const time = DateTime.local().endOf("minute");
  const dateFormatting =
    task.routine?.schedule?.rules?.length === 1 &&
    task.routine.schedule?.rules[0].freq === "WEEKLY"
      ? "WEEKLY"
      : "DAILY";

  const historyItems = useMemo(
    (): GraphNumberData =>
      task.occasions.edges
        .slice(0, maxOccasions ?? (large ? 8 : 5))
        .map(({ node: { id, occasion, taskExecutions } }, i) => {
          const startAt = DateTime.fromISO(occasion.startAt);
          const endAt = DateTime.fromISO(occasion.endAt);
          const isActive = time > startAt && time < endAt;
          const specifiedTaskExecutions = taskExecutions.filter((te) =>
            sectionMap.has(te.sectionId)
          );
          // if we have group by team we need to group the task executions here
          let total: number | null = null;
          const parts = groups.map((group) => {
            const count =
              i === 0
                ? group.numberValue
                : specifiedTaskExecutions.reduce<null | number>(
                    (prev, cur) =>
                      cur?.numberValue &&
                      group.sectionIds.includes(cur.sectionId)
                        ? (prev ?? 0) + cur.numberValue
                        : prev,
                    null
                  );

            if (count !== null) {
              total = (total ?? 0) + count;
            }
            return {
              weight: count ?? 0,
              color: group.color ?? undefined,
            };
          });

          return {
            id,
            value: total,
            parts,
            isActive,
            label: DateTime.fromISO(occasion.startAt).toFormat(
              dateFormatting === "WEEKLY" ? "v.W" : "d"
            ),
          };
        })
        .reverse(),
    [
      task.occasions.edges,
      maxOccasions,
      large,
      time,
      groups,
      dateFormatting,
      sectionMap,
    ]
  );

  const [formatBars, barUnit] = formatValue(
    task.unit,
    Math.max(
      total.goals?.greaterThan ?? total.goals?.lessThan ?? 0,
      total.numberValue ?? 0
    )
  );
  const bars = useMemo(
    () =>
      getGoalBars(
        total.goals?.greaterThan ?? null,
        formatBars,
        barUnit,
        total.goals?.lessThan ?? null
      ),
    [barUnit, formatBars, total.goals?.greaterThan, total.goals?.lessThan]
  );

  const _op = useCallback(() => {
    onPress(
      task.occasions.edges.map(({ node }) => node.occasion.sequenceNumber)
    );
  }, [onPress, task.occasions.edges]);

  const title = task.routine.type === "KPI" ? task.routine.title : task.title;
  const subtitle = task.routine.type === "KPI" ? "" : task.routine.title;
  if (large) {
    return (
      <TouchableBox
        onPress={_op}
        style={{ flex: 1, flexDirection: "row" }}
        color="surface"
      >
        <Box style={{ flex: 1 }}>
          <View style={{ flexDirection: "row", justifyContent: "flex-start" }}>
            <AHWidgetTitle color="darkerText">
              {title}
              {sections.length === 1 ? (
                <SingleSectionTitle section={sections[0]} />
              ) : null}
            </AHWidgetTitle>
          </View>
          <AHText spaceHorizontal="medium" type="detail" numberOfLines={1}>
            {subtitle}
          </AHText>
          <GraphContainer hasGoals={bars.length > 0}>
            <GraphNumber
              animated={interactive !== false}
              data={historyItems}
              goals={bars}
            />
          </GraphContainer>
        </Box>
        {inProgressOccasion ? (
          <SingleTaskInProgress
            unit={task.unit}
            items={groups}
            total={total}
            lastUpdated={lastUpdated}
          />
        ) : null}
      </TouchableBox>
    );
  }
  return (
    <TouchableBox onPress={_op} style={{ flex: 1 }} color="surface">
      <Box
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box style={{ flex: 1 }}>
          <AHWidgetTitle color="darkerText">
            {title}{" "}
            {sections.length === 1 ? (
              <SingleSectionTitle section={sections[0]} />
            ) : null}
          </AHWidgetTitle>
          <AHText spaceHorizontal="medium" type="detail" numberOfLines={1}>
            {subtitle}
          </AHText>
        </Box>
        <SmallInProgress
          unit={task.unit}
          goalLargerThan={total.goals?.greaterThan ?? null}
          goalLessThan={total.goals?.lessThan ?? null}
          sum={total.numberValue}
        />
      </Box>
      <GraphContainer hasGoals={bars.length > 0}>
        <GraphNumber
          animated={interactive !== false}
          data={historyItems}
          goals={bars}
        />
      </GraphContainer>
    </TouchableBox>
  );
};

export const WidgetTaskSingleNumber = React.memo(
  WidgetTaskSingleNumberOccasionPageC
);
